import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useTheme } from 'src/context/ThemeContext';
import { ThemeConfig } from 'src/context/ThemeContext';
import { createComponentConfig, TEFComponentConfig } from 'src/tef/components/config/componentConfig';
import { ToastProvider } from 'src/tef/components/feedback/Toast';

interface TEFContextType extends TEFComponentConfig {
  colorMode: 'light' | 'dark';
  displayMode: 'comfort' | 'compact' | 'easy-reader';
}

interface TEFProviderProps {
  children: ReactNode;
  theme?: Partial<ThemeConfig>;
}

const TEFContext = createContext<TEFContextType | undefined>(undefined);

export const TEFProvider: React.FC<TEFProviderProps> = ({ children, theme: customTheme }) => {
  const appTheme = useTheme();

  const tefTheme = useMemo(() => {
    const baseConfig = createComponentConfig(appTheme.theme);

    const mergedTheme: TEFComponentConfig = customTheme
      ? {
          colors: { ...baseConfig.colors, ...customTheme.colors },
          typography: { ...baseConfig.typography, ...customTheme.typography },
          spacing: { ...baseConfig.spacing, ...customTheme.spacing },
          borderRadius: { ...baseConfig.borderRadius, ...customTheme.borderRadius },
          form: { ...baseConfig.form, ...customTheme.form },
        }
      : baseConfig;

    return {
      ...mergedTheme,
      colorMode: appTheme.colorMode,
      displayMode: appTheme.displayMode,
    };
  }, [appTheme, customTheme]);

  return (
    <TEFContext.Provider value={tefTheme}>
      <ToastProvider>{children}</ToastProvider>
    </TEFContext.Provider>
  );
};

export const useTEF = (): TEFContextType => {
  const context = useContext(TEFContext);
  if (!context) {
    throw new Error('useTEF must be used within TEFProvider');
  }
  return context;
};
